<template lang="pug">
.insights-timeline-filter.flex-1(v-if="visible")
  label Showing week of
  AppSelect(
    v-model="selectedTabId"
    :options="tabs"
    :select2-options="select2Options"
    @input="onSelect"
  )
</template>

<script>
import { formatInternalDate, getWeeksInPeriod } from '~/utils/time_utils';
import { mapState, mapActions } from 'vuex';
import { InsightsTracking } from '~/mixins';

// The current implementation of AppTab/AppTabs doesn't allow us to replace tabs reactively.
// Because of this, we are unable to use those components to display the correct list of tabs
// - we would be _appending_ new tabs whenever we changed a filter (e.g. from this
// month to last month) instead of having a new batch of tabs.
// This is why we have implemented custom tabs for this particular scenario.

export default {
  name: 'InsightsTimelineFilter',
  mixins: [InsightsTracking],
  data: () => ({
    selectedTabId: '',
    select2Options: Object.freeze({
      width: '100%',
      placeholder: 'Select Week',
    }),
  }),
  computed: {
    ...mapState(['filters']),
    filtersHash() {
      return `${this.filters.date.to}${this.filters.date.from}${this.filters.user_id}${this.filters.time_zone}`;
    },
    /**
     * Checks if the current tab is the timeline tab.
     * This filter component is rendered on both the timeline tab and other tabs
     * (e.g., performance tab where timeline widget is also shown).
     * We only want to fetch data independently when on the timeline tab.
     * On other tabs, the parent tab handles widget fetching.
     */
    isTimelineTab() {
      return this.filters.tab === 'timeline';
    },
    weeksInPeriod() {
      return getWeeksInPeriod(this.filters.date.from, this.filters.date.to, true).map((week) => {
        const from = moment(this.filters.date.from);
        const to = moment(this.filters.date.to);
        const start = week.start.isBefore(from) ? from : week.start;
        const end = week.end.isAfter(to) ? to : week.end;

        return { ...week, start, end };
      });
    },
    tabs() {
      // Add a label to each week (startMonth startDay - endMonth endDay)
      // endMonth is only appended if it differs from startMonth
      // If start and end date are the same, show only startDay
      const dateFormat = TimeUtils.Formats.date.pretty;

      return (
        this.weeksInPeriod &&
        this.weeksInPeriod.map((w) => {
          const start = w.start.format(dateFormat);
          const end = w.end.format(dateFormat);
          const text = moment(w.start).isSame(moment(w.end), 'day') ? start : `${start} - ${end}`;

          return { id: w.id, text };
        })
      );
    },
    visible() {
      return this.weeksInPeriod.length > 1;
    },
  },
  watch: {
    filtersHash: function () {
      this.filter();
    },
  },
  created() {
    this.filter();
  },
  methods: {
    ...mapActions(['fetchTimeline', 'setTimelineFilters']),
    toggleTrackProperties() {
      window.analytics?.track('Timeline date toggle', {
        title: 'Timeline',
        actionType: 'toggle',
        ...this.commonProps(),
      });
    },
    async onSelect(weekId) {
      await this.filter(weekId);
      this.toggleTrackProperties();
    },
    async filter(weekId = null) {
      let from, to;

      // We either filter for a particular week or use the default week pick.
      if (!weekId) {
        this.setDefaultselectedTabId();
        weekId = this.selectedTabId;
      } else {
        this.selectTab(weekId);
      }

      if (this.weeksInPeriod.length > 1) {
        const week = this.weeksInPeriod.find((w) => w.id === weekId);

        from = formatInternalDate(moment(week.start));

        to = formatInternalDate(moment(week.end));
      } else {
        from = this.filters.date.from;
        to = this.filters.date.to;
      }

      await this.setTimelineFilters({ from, to });
      if (this.isTimelineTab) {
        await this.fetchTimeline();
      }
    },
    setDefaultselectedTabId() {
      if (this.visible) {
        const now = moment();
        const found = this.weeksInPeriod.find((w) => now.diff(w.start, 'days') >= 0 && now.diff(w.end, 'days') < 0);
        // If we are unable to find a week where "now" is contained, the correct selection is the last week
        // (and this means we're viewing 'last month')
        const selectedTabId = found ? found.id : this.weeksInPeriod[this.weeksInPeriod.length - 1].id;

        this.selectTab(selectedTabId);
      }
    },
    selectTab(id) {
      this.selectedTabId = id;
    },
    getTabClasses(tab) {
      return tab.id === this.selectedTabId ? 'active' : '';
    },
  },
};
</script>
